import React, { useState } from 'react';
import { Phone, Mail, MessageSquare, Send, Building2, FileText, MapPin } from 'lucide-react';
import GlassCard from './ui/GlassCard';
import ScrollReveal from './ui/ScrollReveal';
import { trackEvent } from '../utils/analytics';

export const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Define the endpoint and form data
    const endpoint = 'https://formsubmit.co/admin@sudiptadhara.in';

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        // Reset form
        setFormData({ name: '', email: '', message: '' });
        // Show success message
        alert('Message sent successfully!');
      } else {
        throw new Error('Failed to send message. Please try again later.');
      }
    } catch (error) {
      console.error('Error:', error);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      alert(error.message);
    }
  };

  const openWhatsApp = () => {
    trackEvent('contact-whatsapp');
    const phone = '+919851904515';
    const message = encodeURIComponent('Hello! I visited your portfolio website and would like to connect with you.');
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
  };

  return (
    <section id="contact" className="py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-ink">Get In Touch</h2>
          <div className="w-24 h-1 bg-brand mx-auto"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Left: Contact info card */}
          <ScrollReveal>
            <GlassCard className="p-6 h-full">
              <h3 className="text-2xl font-bold text-ink mb-6">Contact Information</h3>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="p-3 rounded-full mr-4 bg-white/10"><Phone size={20} className="text-brand" /></div>
                  <div>
                    <h4 className="font-semibold text-ink mb-1">Phone</h4>
                    <p className="text-ink-dim">+91 9851 904 515</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="p-3 rounded-full mr-4 bg-white/10"><Mail size={20} className="text-brand" /></div>
                <div>
                  <h4 className="font-semibold text-ink mb-1">Email</h4>
                  <p className="text-ink-dim">admin@sudiptadhara.in</p>
                </div>
                </div>
              </div>
            </GlassCard>
          </ScrollReveal>

          {/* Right: WhatsApp + Available For */}
          <div className="space-y-8">
            <ScrollReveal>
              <GlassCard className="p-6">
                <h4 className="font-semibold text-ink mb-4 flex items-center">
                  <MessageSquare size={20} className="text-brand mr-2" />
                  Connect on WhatsApp
                </h4>
                <p className="text-ink-dim mb-4">Feel free to reach out to me directly on WhatsApp for a quick response.</p>
                <button 
                  onClick={openWhatsApp} 
                  className="flex items-center justify-center gap-2 w-full py-3 bg-green-500/90 hover:bg-green-500 text-white font-medium rounded-mdx transition-colors"
                  data-umami-event="contact-whatsapp"
                >
                  <MessageSquare size={18} />
                  Chat on WhatsApp
                </button>
              </GlassCard>
            </ScrollReveal>

            <ScrollReveal>
              <GlassCard className="p-6">
                <h4 className="font-semibold text-ink mb-4">Available For</h4>
                <div className="flex flex-wrap gap-2">
                  {['Generative AI Engineer', 'RAG Systems', 'Agentic AI', 'LangChain', 'LangGraph', 'Vector DBs', 'Web Development', 'Mobile Apps', 'Consulting', 'Technical Writing'].map((item, index) => (
                    <span key={index} className={`px-3 py-1 rounded-full text-sm ${index < 6 ? 'bg-brand/20 border border-brand/40 text-ink' : 'bg-white/10 text-ink'}`}>
                      {item}
                    </span>
                  ))}
                </div>
              </GlassCard>
            </ScrollReveal>
          </div>
        </div>

        {/* Business Details Section */}
        <div className="mt-12 max-w-4xl mx-auto">
          <ScrollReveal>
            <GlassCard className="p-6">
              <h3 className="text-2xl font-bold text-ink mb-6 flex items-center">
                <Building2 size={24} className="text-brand mr-3" />
                Business Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="p-2 rounded-full mr-3 bg-white/10"><Building2 size={18} className="text-brand" /></div>
                    <div>
                      <h4 className="font-semibold text-ink mb-1">Trade Name</h4>
                      <p className="text-ink-dim">Sudipta Dhara</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="p-2 rounded-full mr-3 bg-white/10"><FileText size={18} className="text-brand" /></div>
                    <div>
                      <h4 className="font-semibold text-ink mb-1">GSTIN</h4>
                      <p className="text-ink-dim">19AVZPD8368C1ZY</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="p-2 rounded-full mr-3 bg-white/10"><Mail size={18} className="text-brand" /></div>
                    <div>
                      <h4 className="font-semibold text-ink mb-1">Business Email</h4>
                      <p className="text-ink-dim">admin@sudiptadhara.in</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="p-2 rounded-full mr-3 bg-white/10 flex-shrink-0"><MapPin size={18} className="text-brand" /></div>
                  <div>
                    <h4 className="font-semibold text-ink mb-1">Registered Address</h4>
                    <p className="text-ink-dim leading-relaxed">
                      A-T6-1602, The Soul, New Town,<br />
                      North 24 Parganas, West Bengal, 700135
                    </p>
                  </div>
                </div>
              </div>
            </GlassCard>
          </ScrollReveal>
        </div>
          
          {/*<div>*/}
          {/*  <div className="bg-white rounded-lg shadow-md p-8">*/}
          {/*    <h3 className="text-2xl font-bold text-gray-900 mb-6">Send Me a Message</h3>*/}
          {/*    */}
          {/*    <form onSubmit={handleSubmit}>*/}
          {/*      <div className="mb-6">*/}
          {/*        <label htmlFor="name" className="block text-gray-700 font-medium mb-2">Name</label>*/}
          {/*        <input*/}
          {/*          type="text"*/}
          {/*          id="name"*/}
          {/*          name="name"*/}
          {/*          value={formData.name}*/}
          {/*          onChange={handleChange}*/}
          {/*          required*/}
          {/*          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"*/}
          {/*          placeholder="Your name"*/}
          {/*        />*/}
          {/*      </div>*/}
          {/*      */}
          {/*      <div className="mb-6">*/}
          {/*        <label htmlFor="email" className="block text-gray-700 font-medium mb-2">Email</label>*/}
          {/*        <input*/}
          {/*          type="email"*/}
          {/*          id="email"*/}
          {/*          name="email"*/}
          {/*          value={formData.email}*/}
          {/*          onChange={handleChange}*/}
          {/*          required*/}
          {/*          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"*/}
          {/*          placeholder="Your email address"*/}
          {/*        />*/}
          {/*      </div>*/}
          {/*      */}
          {/*      <div className="mb-6">*/}
          {/*        <label htmlFor="message" className="block text-gray-700 font-medium mb-2">Message</label>*/}
          {/*        <textarea*/}
          {/*          id="message"*/}
          {/*          name="message"*/}
          {/*          value={formData.message}*/}
          {/*          onChange={handleChange}*/}
          {/*          required*/}
          {/*          rows={6}*/}
          {/*          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all resize-none"*/}
          {/*          placeholder="Your message"*/}
          {/*        ></textarea>*/}
          {/*      </div>*/}
          {/*      */}
          {/*      <button*/}
          {/*        type="submit"*/}
          {/*        className="flex items-center justify-center gap-2 w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"*/}
          {/*      >*/}
          {/*        <Send size={18} />*/}
          {/*        Send Message*/}
          {/*      </button>*/}
          {/*    </form>*/}
          {/*  </div>*/}
          {/*</div>*/}
      </div>
    </section>
  );
};
